import React, { useCallback, useContext, useEffect, useMemo } from 'react';

import { Snackbar, Box, Typography, IconButton, Fade, useTheme, alpha } from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import CloseIcon from '@mui/icons-material/Close';
import { tokens } from '../theme';
import { SnackBarContext } from '../Context/SnackBarContextProvider';

// Using React.memo to prevent re-renders when props haven't changed
const SnackbarAlert = React.memo(() => {
  const { openSnackBar, infoAlert, handleCloseSnackBar } = useContext(SnackBarContext);

  const theme = useTheme();
  const color = tokens(theme.palette.mode);

  // Prevent event propagation
  const handleClose = useCallback((event, reason) => {
    event?.stopPropagation();
    // Ignore clickaway; only close via explicit close button or auto-hide
    if (reason === 'clickaway') return;
    handleCloseSnackBar(event, reason);
  }, [handleCloseSnackBar]);

  // Dev logs to correlate with dialog state, only in development
  useEffect(() => {
    if (process.env.NODE_ENV !== 'production') {
      // eslint-disable-next-line no-console
      console.debug('[SnackbarAlert] open=', openSnackBar, 'message=', infoAlert?.message);
    }
  }, [openSnackBar, infoAlert?.message]);

  // Background and icon colors based on severity using Material UI theme
  const getColors = useCallback((severity) => {
    switch (severity) {
      case 'success':
        return { 
          bg: color.green[70], 
          icon: theme.palette.success.main, 
          border: theme.palette.success.light,
          textColor:color.green[750]
        };
      case 'info':
        return { 
          bg: color.skyBlue[150], 
          icon: theme.palette.info.main, 
          border: theme.palette.info.light,
          textColor: color.skyBlue[700]
        };
      case 'warning':
        return { 
          bg: color.Orange[150], 
          icon: theme.palette.warning.main, 
          border: theme.palette.warning.light,
          textColor: color.Orange[600]
        };
      case 'error':
        return { 
          bg: color.redAccent[150], 
          icon: theme.palette.error.main, 
          border: theme.palette.error.light,
          textColor: color.redAccent[700]
        };
      default:
        return { 
          bg: alpha(theme.palette.info.light, 0.1), 
          icon: theme.palette.info.main, 
          border: theme.palette.info.light,
          textColor: theme.palette.info.dark
        };
    }
  }, [color, theme.palette]);

  // Use memo for colors calculation to prevent unnecessary recalculations
  const colors = useMemo(() => getColors(infoAlert.severity), [getColors, infoAlert.severity]);

  // Custom icon based on severity with memoization
  const getIcon = useCallback((severity) => {
    const colors = getColors(severity);
    
    const iconStyles = {
      backgroundColor: colors.bg, 
      borderRadius: '30%', 
      padding: "5px",
      display: 'flex', 
      alignItems: 'center', 
      justifyContent: 'center',
      border: `1px solid ${colors.border}`,
      backgroundColor: colors.icon,
      color: 'white'
    };
    
    switch (severity) {
      case 'success':
        return (
          <Box sx={iconStyles}>
            <CheckCircleOutlineIcon sx={{ fontSize: 30 }} />
          </Box>
        );
      case 'error':
        return (
          <Box sx={iconStyles}>
            <ErrorOutlineIcon sx={{ fontSize: 30 }} />
          </Box>
        );
      case 'warning':
        return (
          <Box sx={iconStyles}>
            <WarningAmberOutlinedIcon sx={{ fontSize: 30 }} />
          </Box>
        );
      case 'info':
      default:
        return (
          <Box sx={iconStyles}>
            <InfoOutlinedIcon sx={{ fontSize: 30 }} />
          </Box>
        );
    }
  }, [getColors]);

  // Memoize the icon
  const icon = useMemo(() => getIcon(infoAlert.severity), [getIcon, infoAlert.severity]);

  // Don't render if there's no message
  if (!infoAlert || !infoAlert.message) {
    return null;
  }

  return (
    <Snackbar
      key={infoAlert.message}
      open={openSnackBar}
      autoHideDuration={8000}
      onClose={handleClose}
      onClick={(e) => e.stopPropagation()}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      TransitionComponent={Fade}
      TransitionProps={{ 
        timeout: {
          enter: 500,
          exit: 400
        }
      }}
      sx={{ 
        bottom: { xs: 16, sm: 24 }
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          backgroundColor: colors.bg,
          borderRadius: '10px',
          padding: '6px 16px',
          boxShadow: `0 1px 6px ${colors.textColor}`,
          minWidth: { xs: '80vw', sm: '300px' },
          maxWidth: { xs: '90vw', sm: '400px' },
          borderLeft: `6px solid ${colors.border}`,
          transition: 'all 0.3s ease'
        }}
      >
        <Box sx={{ mr: 1.5, display: 'flex', alignItems: 'center' }}>
          {icon}
        </Box>
        <Typography 
          sx={{ 
            flexGrow: 1, 
            color: colors.textColor,
            fontWeight: 600,
            fontSize: "16px"
          }}
        >
          {infoAlert.message}
        </Typography>
        <IconButton
          size="small"
          aria-label="close"
          onClick={handleClose}
          sx={{ 
            ml: 1, 
            padding: '4px',
            color: colors.textColor,
            '&:hover': {
              backgroundColor: 'rgba(0, 0, 0, 0.04)'
            }
          }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>
    </Snackbar>
  );
});

export default SnackbarAlert;